import { createDebug, createNadDebug } from './debugLog'

const CONTENT_POLICY_PATTERNS = [
  'content_policy_violation',
  'content policy',
  'sensitive content',
  'safety filter',
  'safety system',
  'blocked by',
  'policy violation',
  'partner_validation_failed',
  'copyright restrictions',
  // Vertex AI (Veo / Imagen) safety refusal: "the input image violates
  // Vertex AI's usage guidelines". Singular matches both forms.
  'usage guideline',
]

const INTEGRITY_PATTERNS = ['content-safety screening', 'integrity screening', 'csam']

/**
 * The subset of a react-i18next `t` this module needs — same shape as the one
 * `humanizeAuthError` / `mapAuthError` already take, so a real
 * `useTranslation('canvas').t` is assignable without a cast and tests can pass a
 * plain stub.
 *
 * Every message this sanitizer AUTHORS resolves through a `canvas:nodeError.*`
 * key at render time rather than being baked in English. It is deliberately NOT
 * persisted: `data.error` lives in YDoc and is shared across collaborators, so a
 * localized string frozen into it would show in the wrong language for a
 * differently-localed collaborator (the same reasoning that produced the
 * `dw-err:` envelope in `nodeErrorCode.ts`).
 *
 * Provider-authored text is still returned verbatim (CLAUDE.md #7) — only
 * Dreamwrite's own wrapper copy is localized.
 */
export type NodeErrorTranslate = (
  key: string,
  params?: Record<string, string | number>,
) => string

const RATE_LIMIT_PATTERNS = [
  'rate limit',
  'rate_limit',
  'too many requests',
  '429',
  'quota exceeded',
]

const TIMEOUT_PATTERNS = ['timed out', 'timeout', 'deadline exceeded']

const TRANSIENT_ALL_PROVIDER_CAUSE_PATTERNS = [
  'connection reset',
  'connection refused',
  'connection error',
  'network error',
  'service unavailable',
  'temporarily unavailable',
]

const PROVIDER_AUTH_ERROR_PATTERNS = [
  'invalid api key',
  'invalid_api_key',
  'api key is invalid',
  'api key invalid',
  'authentication failed',
  'authorization failed',
  'invalid authorization',
  'missing api key',
  'provider submit error (401)',
  'provider status error (401)',
  'provider result fetch error (401)',
]

// Mirror provider_models.GATEWAY_BRANDED_PROVIDER_ERROR_PREFIXES. These exact
// gateway-owned prefixes identify provider 4xx details that the AI gateway has
// already bounded and credential-redacted.
export const GATEWAY_BRANDED_PROVIDER_ERROR_PREFIXES: readonly string[] = [
  'byteplus modelark submit error',
  'byteplus modelark task poll error',
]

// Provider job-outcome failure markers (ai-gateway maps these to HTTP 422): the
// provider accepted the request but could not produce the output. The detail
// carries the provider's specific reason, so surface it faithfully (principle #7)
// instead of collapsing it to a generic message via the length/noise catch-all.
const PROVIDER_OUTCOME_ERROR_PATTERNS = [
  'provider job failed',
  'no video url found',
  'no request_id and no video',
  'provider returned non-json during',
  'provider returned non-object json during',
  'provider transport failure',
]

const GATEWAY_SOURCE_BOUNDED_PREFIXES = [
  'byteplus modelark provider job failed:',
  'byteplus modelark no video url found:',
  'provider returned non-json during',
  'provider returned non-object json during',
  'provider transport failure',
]

const AI_GATEWAY_422_DETAIL_RE =
  /^ai gateway request failed: http 422\b[^:]*:\s*([\s\S]*)$/

const AI_GATEWAY_BYTEPLUS_PROVIDER_4XX_RE =
  /^ai gateway request failed: http (4\d\d)\b[^:]*:\s*byteplus modelark (?:submit|task poll) error \((4\d\d)\):/s
// Bound the exact string returned to the caller, including any provider-chain
// wrapper, rather than only the unwrapped gateway candidate used for shape
// validation.
const MAX_GATEWAY_BYTEPLUS_PROVIDER_ERROR_CHARS = 1300
const BYTEPLUS_PROVIDER_ERROR_MARKERS = [
  'byteplus modelark submit error (',
  'byteplus modelark task poll error (',
]

// The API is the authoritative trust boundary and only persists long detail
// for generation nodes. Keep this defensive console cap in sync with
// dreamwright_api.error_sanitizer so a second presentation pass does not erase
// an already-approved image, video, or audio provider error.
const MAX_GENERATION_ERROR_DETAIL_CHARS = 5000
const FAL_MINIMAX_PROVIDER_4XX_RE =
  /^fal\.ai minimax h3 request failed: http (4\d\d)\b[^:]*:\s*fal\.ai minimax h3 provider error \((4\d\d)\):/s
const FAL_MINIMAX_PROVIDER_ERROR_MARKER = 'fal.ai minimax h3 provider error ('
const FAL_MINIMAX_VALIDATION_STATUSES = new Set([400, 422])
const providerErrorSurfaceLog = createNadDebug('dw:provider-error-surfacing:sanitizer')

function outerGatewayErrorText(text: string): string {
  let candidate = text.trim()
  const lowered = candidate.toLowerCase()
  if (lowered.startsWith('all providers failed')) {
    const lastErrorMarker = 'last error:'
    const markerIndex = lowered.indexOf(lastErrorMarker)
    if (markerIndex >= 0) {
      candidate = candidate.slice(markerIndex + lastErrorMarker.length).trim()
    }
  }
  return candidate
}

function gateway422Detail(lowered: string): string | undefined {
  const candidate = outerGatewayErrorText(lowered)

  return AI_GATEWAY_422_DETAIL_RE.exec(candidate)?.[1]?.trim()
}

function gatewayBytePlusProvider4xxText(text: string): string | undefined {
  const candidate = outerGatewayErrorText(text)
  const match = AI_GATEWAY_BYTEPLUS_PROVIDER_4XX_RE.exec(candidate.toLowerCase())
  return match !== null && match[1] === match[2] ? candidate : undefined
}

function falMinimaxProvider4xxMatch(
  text: string,
): { text: string; status: number } | undefined {
  const candidate = outerGatewayErrorText(text)
  const match = FAL_MINIMAX_PROVIDER_4XX_RE.exec(candidate.toLowerCase())
  if (match === null || match[1] !== match[2]) return undefined
  const status = Number(match[1])
  return FAL_MINIMAX_VALIDATION_STATUSES.has(status)
    ? { text: candidate, status }
    : undefined
}

function logFalMinimaxSurfacingDecision(
  matchedMarker: string,
  bucket: string,
  surfaced: boolean,
): void {
  providerErrorSurfaceLog({
    event: 'provider-error-surfacing:classified',
    matched_marker: matchedMarker,
    bucket,
    provider_detail_surfaced: surfaced,
  })
}

const TOO_MANY_REFERENCE_IMAGE_PREFIXES = [
  "too many reference images on 'in_characters/in_locations/in_objects':",
  "too many reference images on 'in_images':",
]

const AUDIO_GENERATION_FAILURE_MAX_LENGTH = 1000
const AUDIO_GENERATION_TRUSTED_DETAIL_RE =
  /^TTS line .{1,300} Automatic TTS fitting supports at most .{1,40}\. Shorten or split the line, or regenerate the clip with more dialogue time\.$/
const audioFailureSurfaceLog = createDebug('dw:audio-generation-failure-surface:sanitizer')
const outputTruncationSurfaceLog = createNadDebug('dw:director-output-truncation:sanitizer')

// Provider output-length truncation: the generation aborted because the output
// exceeded the model's token limit (Gemini `finish_reason=MAX_TOKENS`,
// `finishReason=MAX_TOKENS`, or `stop_reason=max_tokens`) or the
// structured JSON body was cut off mid-parse (pydantic-core "EOF while parsing").
// This is not retryable as-is — the request itself is too large — so the banner
// gives an actionable "make it shorter / split it" summary instead of raw
// provider jargon. The raw reason remains available to the Director and backend
// logs; UI text, including native tooltips, stays behind this sanitizer.
//
// This sanitizer is SHARED across every node type, so the shape is matched
// NARROWLY to avoid mislabeling unrelated failures:
//   - MAX_TOKENS is matched ONLY as a *finish reason*
//     (`finish_reason(s)=[MAX_TOKENS]`, `finishReason=MAX_TOKENS`, or
//     `stop_reason=max_tokens`), NOT as a bare substring — so provider
//     input-validation errors like "max_tokens must be greater than zero" or
//     "unsupported parameter: max_tokens" are NOT mislabeled as truncation.
//   - `EOF while parsing` is pydantic-core's cut-off-JSON message; it is NOT the
//     JS `Unexpected end of JSON input` (a generic malformed-response error),
//     which is therefore not matched.
// The generic "did not finish cleanly" is also excluded: SAFETY / RECITATION
// finish reasons emit that same phrase and must route to the content-policy
// bucket (which runs first). Keep this from silently converging with the backend
// `_is_truncation_validation_error`
// (agentic_workflow_backend/.../agents/script_writer/agent.py).
const OUTPUT_TRUNCATION_RE = /(?:finish_?reasons?|stop_reason)\s*=\s*\[?\s*'?(?:[a-z_]+\.)?max_tokens|eof while parsing/i

const STRUCTURED_NOISE = /\{'loc'|{"loc"|Traceback \(most recent|https?:\/\/docs\.\w+\.\w+\/errors/i

function isTrustedAudioGenerationFailure(raw: string, lowered: string): boolean {
  if (
    raw.length > AUDIO_GENERATION_FAILURE_MAX_LENGTH ||
    raw.includes('\n') ||
    raw.includes('\r') ||
    STRUCTURED_NOISE.test(raw) ||
    INTEGRITY_PATTERNS.some((pattern) => lowered.includes(pattern))
  ) {
    return false
  }
  return AUDIO_GENERATION_TRUSTED_DETAIL_RE.test(raw)
}

/**
 * True when a node error is an output-length truncation (Gemini
 * `finish_reason=MAX_TOKENS` or a cut-off JSON body). Exposed so callers can
 * instrument/branch on this class without re-deriving the marker set — the
 * sanitizer itself is the single source of truth for the patterns.
 */
export function isOutputTruncationError(raw: string | undefined): boolean {
  if (!raw) return false
  return OUTPUT_TRUNCATION_RE.test(raw)
}

export function sanitizeNodeError(
  raw: string | undefined,
  t: NodeErrorTranslate,
  // Set only for generation surfaces whose backend detail has already crossed
  // the authoritative sanitizer boundary.
  preserveGenerationDetail = false,
): string | undefined {
  if (!raw) return raw

  const lowered = raw.toLowerCase()
  const gatewayDetail = gateway422Detail(lowered)
  const maxDetailChars = preserveGenerationDetail ? MAX_GENERATION_ERROR_DETAIL_CHARS : 200
  const detailIsDisplayable =
    Array.from(raw).length <= maxDetailChars && !STRUCTURED_NOISE.test(raw)

  // Integrity/CSAM findings are deliberately non-leaky and outrank every
  // passthrough path, including Dreamwrite-authored audio envelopes.
  if (INTEGRITY_PATTERNS.some((pattern) => lowered.includes(pattern))) {
    return t('nodeError.integrityBlocked')
  }

  // The agent names the render job, stage and original worker reason. Keep it
  // intact in the expandable message rather than replacing it with a generic
  // timeout/length warning (principle 7).
  if (preserveGenerationDetail && lowered.startsWith('timeline render failed for video editor ')) {
    return raw
  }

  // Preserve only bounded, single-line Dreamwrite-authored remediation.
  if (isTrustedAudioGenerationFailure(raw, lowered)) {
    audioFailureSurfaceLog('trusted remediation passthrough length=%d', Array.from(raw).length)
    return raw
  }

  if (lowered.startsWith('storyboard generation was blocked')) {
    return raw
  }

  // Preserve Dreamwrite's known, actionable reference-cap validation even
  // when it exceeds the generic 200-character banner guard.
  if (TOO_MANY_REFERENCE_IMAGE_PREFIXES.some((prefix) => lowered.startsWith(prefix))) {
    return raw
  }

  // Preserve the complete reason attributed by the native fal 400/422 boundary,
  // including provider policy details and JSON bodies, before generic rewrites.
  if (lowered.includes(FAL_MINIMAX_PROVIDER_ERROR_MARKER)) {
    const providerMatch = falMinimaxProvider4xxMatch(raw)
    if (providerMatch === undefined) {
      logFalMinimaxSurfacingDecision(FAL_MINIMAX_PROVIDER_ERROR_MARKER, 'generic', false)
      return t('nodeError.generationFailed')
    }
    logFalMinimaxSurfacingDecision(
      `fal.ai minimax h3 provider error (${providerMatch.status})`,
      'fal-provider-validation',
      true,
    )
    return raw
  }

  if (CONTENT_POLICY_PATTERNS.some((p) => lowered.includes(p))) {
    if (preserveGenerationDetail && detailIsDisplayable) {
      return raw
    }
    return t('nodeError.contentPolicy')
  }

  // BytePlus submit/task-poll HTTP 4xx is a caller-owned provider rejection.
  // Preserve its exact gateway-bounded/redacted status/code/body above the
  // generic 200-character guard, but only AFTER the integrity and content-policy
  // buckets have applied their dedicated non-leaky messages. Keep this before
  // generic auth/rate rewrites. Only the valid outer 4xx shape is trusted; 5xx,
  // unsupported operations, nested lookalikes, source-bound violations, and
  // structured noise remain generic.
  if (BYTEPLUS_PROVIDER_ERROR_MARKERS.some((marker) => lowered.includes(marker))) {
    const gatewayError = gatewayBytePlusProvider4xxText(raw)
    if (
      gatewayError === undefined ||
      Array.from(raw).length > MAX_GATEWAY_BYTEPLUS_PROVIDER_ERROR_CHARS ||
      STRUCTURED_NOISE.test(gatewayError)
    ) {
      return t('nodeError.generationFailed')
    }
    return raw
  }

  // Output-length truncation (MAX_TOKENS / cut-off body). Runs AFTER the
  // content-policy bucket (a genuine SAFETY/RECITATION block routes there first)
  // and BEFORE the "all providers failed" and length/noise catch-alls — so its
  // actionable guidance wins even when the raw error exceeds the 200-char guard.
  if (OUTPUT_TRUNCATION_RE.test(lowered)) {
    outputTruncationSurfaceLog({
      event: 'output-truncation-sanitized',
      raw_length: Array.from(raw).length,
    })
    return t('nodeError.outputTruncated')
  }

  if (PROVIDER_AUTH_ERROR_PATTERNS.some((p) => lowered.includes(p))) {
    return raw
  }

  if (lowered.startsWith('byom run url invalid')) {
    return raw
  }

  // Provider job-outcome failure (gateway 422). Surface the provider's specific
  // reason verbatim (principle #7). Runs AFTER the content-policy safety bucket
  // (its "adjust your prompt" advice is correct for a terminal refusal) but
  // BEFORE the rate-limit/timeout buckets, whose "try again" advice would
  // mislead for a *terminal* (non-retryable) job-outcome failure, and before the
  // generic "all providers failed" collapse. The length / structured-noise guard
  // is still applied inline so a provider that echoes a traceback, a {'loc': ...}
  // validation blob, or an oversized dump can't reach the frontend verbatim.
  if (PROVIDER_OUTCOME_ERROR_PATTERNS.some((p) => lowered.includes(p))) {
    if (
      gatewayDetail !== undefined &&
      GATEWAY_SOURCE_BOUNDED_PREFIXES.some((prefix) => gatewayDetail.startsWith(prefix))
    ) {
      return raw
    }
    if (!detailIsDisplayable) {
      return t('nodeError.generationFailed')
    }
    return raw
  }

  if (lowered.includes('ai gateway request failed: http 401')) {
    return t('nodeError.generationFailed')
  }

  if (RATE_LIMIT_PATTERNS.some((p) => lowered.includes(p))) {
    return t('nodeError.rateLimit')
  }

  if (TIMEOUT_PATTERNS.some((p) => lowered.includes(p))) {
    return t('nodeError.timeout')
  }

  if (lowered.includes('all providers failed')) {
    const lastErrorMarker = 'last error:'
    const lastErrorIndex = lowered.indexOf(lastErrorMarker)
    const lastError = lastErrorIndex >= 0
      ? lowered.slice(lastErrorIndex + lastErrorMarker.length)
      : ''
    const hasSpecificCause = lastError.trim().length > 0
    const hasTransientCause = TRANSIENT_ALL_PROVIDER_CAUSE_PATTERNS.some((pattern) =>
      lastError.includes(pattern),
    )

    // Keep a bounded, actionable terminal cause visible. This mirrors the API
    // sanitizer so a later frontend pass cannot replace the exact error that
    // the API persisted with a generic provider-exhaustion message. Transient
    // transport failures retain the existing retry guidance.
    if (
      hasSpecificCause &&
      !hasTransientCause &&
      detailIsDisplayable
    ) {
      return raw
    }
    return t('nodeError.allProvidersFailed')
  }

  if (!detailIsDisplayable) {
    return t('nodeError.generationFailed')
  }

  return raw
}
